import { useEffect, useState } from "react";
import { PERIODS, api, inr, nfmt } from "./utils";
import WhatsAppHealthCard from "./WhatsAppHealthCard";
import { Buildings, Users, Receipt, CurrencyInr, Warning, PaperPlaneTilt, TrendUp } from "@phosphor-icons/react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar, Legend } from "recharts";

const KPI = ({ label, value, sub, icon: Icon, accent = "#FFFFFF", testid }) => (
  <div data-testid={testid} className="nb-card p-4 sm:p-5 flex flex-col gap-2" style={{ background: accent }}>
    <div className="flex items-center justify-between">
      <span className="text-[10px] font-bold tracking-[0.2em] uppercase">{label}</span>
      <div className="w-8 h-8 bg-white border-2 border-black rounded-md flex items-center justify-center">
        <Icon size={16} weight="bold" />
      </div>
    </div>
    <div className="font-heading font-black text-3xl sm:text-4xl tracking-tighter">{value}</div>
    {sub && <p className="text-[11px] font-semibold text-[#4A4A4A]">{sub}</p>}
  </div>
);

export default function AdminOverview() {
  const [period, setPeriod] = useState("last30");
  const [custom, setCustom] = useState({ start: "", end: "" });
  const [ov, setOv] = useState(null);
  const [ts, setTs] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setErr("");
    try {
      const q = new URLSearchParams({ period });
      if (period === "custom" && custom.start && custom.end) {
        q.set("start", custom.start); q.set("end", custom.end);
      }
      const [a, b] = await Promise.all([
        api.get(`/admin/overview?${q.toString()}`),
        api.get(`/admin/timeseries?${q.toString()}`),
      ]);
      setOv(a.data); setTs(b.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load overview");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);

  const applyCustom = () => { if (custom.start && custom.end) load(); };

  const chartData = ts?.labels?.map((d, i) => ({
    date: d.slice(5),
    businesses: ts.businesses[i], customers: ts.customers[i],
    invoices: ts.invoices_count[i], invoicesValue: ts.invoices_value[i],
    payments: ts.payments_count[i], paymentsValue: ts.payments_value[i],
    reminders: ts.reminders_total[i], failed: ts.reminders_failed[i],
  })) || [];

  return (
    <div className="space-y-6" data-testid="admin-overview">
      {/* Filters */}
      <div className="nb-card p-3 flex flex-wrap items-center gap-2" data-testid="admin-date-filter">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            data-testid={`period-${p.key}`}
            onClick={() => setPeriod(p.key)}
            className={`nb-btn text-xs px-3 py-1.5 ${period === p.key ? "bg-[#111] text-white" : "bg-white"}`}
          >
            {p.label}
          </button>
        ))}
        {period === "custom" && (
          <div className="flex flex-wrap items-center gap-2 ml-2">
            <input type="date" data-testid="date-start" value={custom.start} onChange={(e) => setCustom({ ...custom, start: e.target.value })}
              className="nb-input text-xs px-2 py-1.5" />
            <span className="text-xs font-bold">to</span>
            <input type="date" data-testid="date-end" value={custom.end} onChange={(e) => setCustom({ ...custom, end: e.target.value })}
              className="nb-input text-xs px-2 py-1.5" />
            <button data-testid="apply-custom" onClick={applyCustom} className="nb-btn bg-[#4361EE] text-white text-xs px-3 py-1.5">Apply</button>
          </div>
        )}
      </div>

      {err && <div className="nb-card p-3 bg-[#FF6B6B] text-white text-sm font-bold">{err}</div>}

      {/* WhatsApp Health — always at top for owner visibility */}
      <WhatsAppHealthCard />

      {/* Business KPIs */}
      <div>
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#4A4A4A] mb-2">Platform businesses</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KPI label="Total Businesses" value={nfmt(ov?.businesses?.total)} sub={`${ov?.businesses?.new_in_period || 0} new in period`} icon={Buildings} testid="kpi-biz-total" />
          <KPI label="Active in Period" value={nfmt(ov?.businesses?.active_in_period)} sub={`${ov?.businesses?.inactive_in_period || 0} inactive`} icon={TrendUp} accent="#4ECDC4" testid="kpi-biz-active" />
          <KPI label="End Customers" value={nfmt(ov?.end_customers?.total)} sub={`${ov?.end_customers?.new_in_period || 0} new`} icon={Users} accent="#FFD166" testid="kpi-cust-total" />
          <KPI label="With Outstanding" value={nfmt(ov?.end_customers?.with_outstanding)} sub={`${ov?.end_customers?.with_overdue || 0} overdue`} icon={Warning} accent="#FFFFFF" testid="kpi-cust-outstanding" />
        </div>
      </div>

      {/* Financials */}
      <div>
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#4A4A4A] mb-2">Invoices & payments</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KPI label="Total Invoices" value={nfmt(ov?.invoices?.total)} sub={`${ov?.invoices?.paid_count || 0} paid · ${ov?.invoices?.outstanding_count || 0} pending`} icon={Receipt} testid="kpi-inv-total" />
          <KPI label="Invoiced Value" value={inr(ov?.invoices?.total_value)} sub={`${ov?.invoices?.overdue_count || 0} overdue`} icon={CurrencyInr} accent="#FFFFFF" testid="kpi-inv-value" />
          <KPI label="Collected" value={inr(ov?.payments?.total_value)} sub={`${ov?.payments?.total_count || 0} payments · ${ov?.payments?.collection_rate_pct || 0}% rate`} icon={CurrencyInr} accent="#4ECDC4" testid="kpi-pay-value" />
          <KPI label="Outstanding" value={inr(ov?.payments?.outstanding)} sub={`Overdue: ${inr(ov?.payments?.overdue_amount)}`} icon={Warning} accent="#FF6B6B" testid="kpi-outstanding" />
        </div>
      </div>

      {/* Plan breakdown */}
      <div>
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#4A4A4A] mb-2">Subscription plan mix</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="plan-breakdown">
          <KPI label="Free" value={nfmt(ov?.businesses?.by_plan?.free)} icon={Buildings} testid="plan-free" />
          <KPI label="Starter" value={nfmt(ov?.businesses?.by_plan?.starter)} icon={Buildings} testid="plan-starter" />
          <KPI label="Business" value={nfmt(ov?.businesses?.by_plan?.business)} icon={Buildings} testid="plan-business" />
          <KPI label="Distributor" value={nfmt(ov?.businesses?.by_plan?.distributor)} icon={Buildings} testid="plan-distributor" />
        </div>
      </div>

      {/* Reminders */}
      <div>
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[#4A4A4A] mb-2">Reminder activity</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KPI label="Reminders Sent" value={nfmt(ov?.reminders?.total)} sub={`${ov?.reminders?.in_period || 0} in period`} icon={PaperPlaneTilt} testid="kpi-rem-total" />
          <KPI label="WhatsApp" value={nfmt(ov?.reminders?.whatsapp)} icon={PaperPlaneTilt} accent="#4ECDC4" testid="kpi-rem-wa" />
          <KPI label="Success" value={nfmt(ov?.reminders?.success)} icon={TrendUp} testid="kpi-rem-ok" />
          <KPI label="Failed" value={nfmt(ov?.reminders?.failed)} icon={Warning} accent="#FF6B6B" testid="kpi-rem-fail" />
        </div>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="nb-card p-4">
          <h3 className="font-heading font-black text-lg mb-2">New businesses & customers</h3>
          <div className="w-full h-56" data-testid="chart-biz-cust">
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="businesses" stroke="#111" strokeWidth={2} name="Businesses" />
                <Line type="monotone" dataKey="customers" stroke="#4361EE" strokeWidth={2} name="Customers" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="nb-card p-4">
          <h3 className="font-heading font-black text-lg mb-2">Invoices & payments (value)</h3>
          <div className="w-full h-56" data-testid="chart-inv-pay">
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => inr(v)} />
                <Legend />
                <Bar dataKey="invoicesValue" fill="#FFD166" name="Invoiced ₹" />
                <Bar dataKey="paymentsValue" fill="#4ECDC4" name="Collected ₹" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="nb-card p-4 lg:col-span-2">
          <h3 className="font-heading font-black text-lg mb-2">Reminders sent vs failed</h3>
          <div className="w-full h-52" data-testid="chart-reminders">
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar dataKey="reminders" fill="#4361EE" name="Sent" />
                <Bar dataKey="failed" fill="#FF6B6B" name="Failed" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
